import React, { useEffect, useState, useMemo, useRef } from 'react';
import { apiKey } from '../../utils/ktcUtils';
import { API_URL, ENDPOINTS } from '../../constants/api';
import { useParams } from 'react-router-dom';

// Grade letters ("A+", "B+") and values like "87.51%" must keep their sign on the
// correct side even inside RTL. dir="ltr" + isolate keeps them in their own LTR run.
const Letter = ({ value }) => (
  <span dir="ltr" style={{ unicodeBidi: 'isolate', display: 'inline-block' }}>
    {value}
  </span>
);

function ArTranscript() {
  const [loading, setLoading] = useState(true);

  const { stdId } = useParams();
  const [student, setStudent] = useState(null);
  const [marks, setMarks] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const url = `${API_URL}/${ENDPOINTS.SYS}/getlink-apikey/transcript-generator-ar/${stdId}`;

    setLoading(true);
    setError(null);

    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        // report can be [] (no rows), [row], or nested arrays when the SQL
        // has several statements (SET / INSERT / SELECT). Find the row that
        // actually has the "student" field.
        const rows = Array.isArray(data?.report) ? data.report.flat(2) : [];
        const row = rows.find((r) => r && r.student);

        if (!row) {
          setError(
            data?.message ||
              data?.error ||
              `لا توجد بيانات كشف درجات للطالب ${stdId}.`
          );
          setLoading(false);
          return;
        }

        const parse = (v) => (typeof v === 'string' ? JSON.parse(v) : v);

        setStudent(parse(row.student));
        setMarks(parse(row.marks) || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Transcript load error:', err);
        setError('تعذر تحميل كشف الدرجات. يرجى المحاولة مرة أخرى.');
        setLoading(false);
      });
  }, [stdId]);

  if (loading) {
    return (
      <div className="text-center my-5" dir="rtl" style={{ fontSize: '12px' }}>
        جاري تحميل كشف الدرجات...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center my-5 text-danger" dir="rtl" style={{ fontSize: '14px' }}>
        {error}
      </div>
    );
  }

  return <OfficialTranscript student={student} marks={marks} />;
}

const OfficialTranscript = ({ student, marks }) => {
  const department = student?.['القسم'] || '-';

  const studentData = {
    std_id: student?.['رقم_الطالب'] || '-',
    name: student?.['اسم_الطالب'] || '-',
    faculty: student?.['الكلية'] || '-',
    department,
    batch: student?.['الدفعة'] || student?.['المجموعة_أو_الصف'] || '-',
    Program_Duration: student?.['مدة_البرنامج'] || '-',
    serial_no: student?.['رقم_التسلسل'] || '-',
    issued_date: student?.['تاريخ_الإصدار'] || '-',
    grand_total: student?.['المجموع_الكلي'] ?? '-',
    average: student?.['المعدل_العام_نسبة'] || '-',
    grade: student?.['التقدير_العام'] || '-',
    gpa: student?.['المعدل_التراكمي'] ?? '-',
    degree_granted:
      student?.['الدرجة_الممنوحة'] ||
      (department !== '-' ? `بكالوريوس في ${department}` : '-'),
    president: student?.['رئيس_الجامعة'] || 'أ.د. أحمد حسن بري',
    registrar: student?.['المسجل_الأكاديمي'] || 'أ. محمد عمر علي',
  };

  const groupedData = useMemo(() => {
    if (!marks || !Array.isArray(marks)) return [];

    const BIG = Number.MAX_SAFE_INTEGER;
    const yearsMap = {};

    marks.forEach((item) => {
      const year = item['السنة_الدراسية'] || 'السنة غير محددة';
      const semester = item['الفصل_الدراسي'] || 'مستوى غير محدد';

      if (!yearsMap[year]) {
        yearsMap[year] = {
          order: Number(item['ترتيب_السنة']) || BIG,
          semesters: {},
        };
      }
      if (!yearsMap[year].semesters[semester]) {
        yearsMap[year].semesters[semester] = {
          order: Number(item['ترتيب_الفصل']) || BIG,
          courses: [],
        };
      }

      yearsMap[year].semesters[semester].courses.push(item);
    });

    // Sort courses by the NUMBER inside the course code
    // e.g. BKS101, ENG102, COM103 (101 < 102 < 103)
    const codeNumber = (code) => {
      const m = String(code || '').match(/\d+/);
      return m ? parseInt(m[0], 10) : Number.MAX_SAFE_INTEGER;
    };
    const sortByCodeNumber = (courses) =>
      [...courses].sort((a, b) => {
        const diff = codeNumber(a['رمز_المادة']) - codeNumber(b['رمز_المادة']);
        return diff !== 0
          ? diff
          : String(a['رمز_المادة'] || '').localeCompare(String(b['رمز_المادة'] || ''));
      });

    return Object.keys(yearsMap)
      .sort((a, b) => yearsMap[a].order - yearsMap[b].order)
      .map((year) => {
        const semObj = yearsMap[year].semesters;
        return {
          yearLabel: year,
          semesters: Object.keys(semObj)
            .sort((a, b) => semObj[a].order - semObj[b].order)
            .map((semKey) => ({
              title: semKey,
              courses: sortByCodeNumber(semObj[semKey].courses),
              gpa: semObj[semKey].courses[0]?.['معدل_الفصل'] ?? '-',
              letter: semObj[semKey].courses[0]?.['تقدير_الفصل'] || '-',
            })),
        };
      });
  }, [marks]);

  const pageRef = useRef(null);

  // ALWAYS print on ONE page: the whole transcript is scaled (zoom) so it
  // fits exactly inside one A4 page, no matter how many levels there are.
  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;

    const PAGE_W_MM = 200; // A4 210mm - 2 x 5mm margin
    const PAGE_H = 1075; // A4 297mm - 2 x 5mm margin, in px, with a small safety gap
    const MAX_SCALE = 1.8;
    const MIN_SCALE = 0.3;

    const applyLayout = (scale) => {
      el.style.setProperty('zoom', String(scale), 'important');
      el.style.setProperty('width', `${PAGE_W_MM / scale}mm`, 'important');
    };

    const beforePrint = () => {
      el.style.setProperty('max-width', 'none', 'important');
      el.style.setProperty('margin', '0 auto', 'important');

      let best = MIN_SCALE;
      for (let s = MAX_SCALE; s >= MIN_SCALE; s -= 0.02) {
        applyLayout(1);
        el.style.setProperty('width', `${PAGE_W_MM / s}mm`, 'important');
        const h = el.offsetHeight; // layout height (zoom = 1)
        if (h * s <= PAGE_H) {
          best = s;
          break;
        }
      }
      applyLayout(best);
    };

    const afterPrint = () => {
      el.style.removeProperty('width');
      el.style.removeProperty('zoom');
      el.style.removeProperty('margin');
      el.style.removeProperty('max-width');
    };

    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);
    return () => {
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, [groupedData]);

  const calculateTotalCredits = (courses) =>
    (courses || []).reduce((sum, item) => sum + (Number(item['الساعات_المعتمدة']) || 0), 0);

  // Shown with ONE decimal like the paper (645.0, 611.0)
  const calculateTotalMarks = (courses) => {
    const total = (courses || []).reduce((sum, item) => sum + (Number(item['الدرجات']) || 0), 0);
    return total.toFixed(1);
  };

  const handlePrint = () => {
    window.print();
  };

  // ------------------------------------------------------------------
  // GRID: 12 physical columns (6 per level). The course-name column is
  // split into two physical columns (nameA + nameB, always used together
  // with colSpan=2) so the summary row below can cut through the name
  // column exactly where it needs to, with straight vertical borders.
  //
  //   per level:  code | nameA | nameB | hours | marks | letter
  //                 1      2       3       4       5       6
  // ------------------------------------------------------------------

  // Renders ONE year as a <tbody> inside the single big table.
  // In RTL, the first level appears on the RIGHT side (المستوى الأول).
  const renderYearRows = (yearBlock, yearIdx) => {
    const sem1 = yearBlock.semesters[0];
    const sem2 = yearBlock.semesters[1];
    const maxRows = Math.max(sem1?.courses?.length || 0, sem2?.courses?.length || 0);
    const rows = Array.from({ length: maxRows });

    const headCells = (
      <>
        <th className="py-1 px-1 text-center align-middle">رمز المادة</th>
        <th colSpan={2} className="py-1 px-1 text-center align-middle">اسم المادة</th>
        <th className="py-1 px-1 text-center align-middle">الساعات المعتمدة</th>
        <th className="py-1 px-1 text-center align-middle">الدرجة</th>
        <th className="py-1 px-1 text-center align-middle">رمز التقدير</th>
      </>
    );

    const courseCells = (c) => (
      <>
        <td
          className="text-center py-1 px-1 text-nowrap"
          style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {c?.['رمز_المادة'] || ''}
        </td>
        <td
          colSpan={2}
          className="py-1 px-1"
          style={{
            textAlign: 'right',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          {c?.['اسم_المادة'] || ''}
        </td>
        <td className="text-center py-1 px-1">{c?.['الساعات_المعتمدة'] ?? ''}</td>
        <td className="text-center py-1 px-1">{c ? c['الدرجات'] ?? '-' : ''}</td>
        <td className="text-center py-1 px-1">
          {c ? <Letter value={c['رمز_التقدير'] || '-'} /> : ''}
        </td>
      </>
    );

    return (
      <tbody key={yearIdx} className="year-block">
        {/* Year: السنة الأولى (2016-2017م) */}
        <tr className="bg-light text-center fw-bold">
          <th colSpan={12} className="py-1 text-center align-middle">
            {yearBlock.yearLabel}
          </th>
        </tr>
        {/* Level: المستوى الأول / المستوى الثاني */}
        <tr className="bg-light text-center fw-bold">
          <th colSpan={6} className="py-1 text-center align-middle">
            {sem1?.title || 'المستوى'}
          </th>
          <th colSpan={6} className="py-1 text-center align-middle">
            {sem2?.title || 'المستوى'}
          </th>
        </tr>
        <tr className="text-center table-light col-head">
          {headCells}
          {headCells}
        </tr>

        {maxRows === 0 ? (
          <tr>
            <td colSpan={12} className="text-center text-muted py-1">
              لا توجد مواد
            </td>
          </tr>
        ) : (
          rows.map((_, idx) => {
            const c1 = sem1?.courses?.[idx];
            const c2 = sem2?.courses?.[idx];
            return (
              <tr key={idx}>
                {courseCells(c1)}
                {courseCells(c2)}
              </tr>
            );
          })
        )}

        {/* المجموع: label (code+name), sum under الساعات, sum under الدرجة, level letter */}
        <tr>
          <td colSpan={3} className="fw-bold py-1 px-1 text-center align-middle">
            المجموع
          </td>
          <td className="text-center fw-bold py-1 px-1 align-middle">
            {sem1 ? calculateTotalCredits(sem1.courses) : ''}
          </td>
          <td className="text-center fw-bold py-1 px-1 align-middle">
            {sem1 ? calculateTotalMarks(sem1.courses) : ''}
          </td>
          <td className="text-center fw-bold py-1 px-1 align-middle">
            {sem1 ? <Letter value={sem1.letter} /> : ''}
          </td>

          <td colSpan={3} className="fw-bold py-1 px-1 text-center align-middle">
            المجموع
          </td>
          <td className="text-center fw-bold py-1 px-1 align-middle">
            {sem2 ? calculateTotalCredits(sem2.courses) : ''}
          </td>
          <td className="text-center fw-bold py-1 px-1 align-middle">
            {sem2 ? calculateTotalMarks(sem2.courses) : ''}
          </td>
          <td className="text-center fw-bold py-1 px-1 align-middle">
            {sem2 ? <Letter value={sem2.letter} /> : ''}
          </td>
        </tr>
        <tr>
          <td colSpan={5} className="fw-bold py-1 px-1 text-center align-middle">
            المعدل العام بالنقاط للفصل الدراسي (GPA)
          </td>
          <td className="text-center fw-bold py-1 px-1 align-middle">{sem1 ? sem1.gpa : ''}</td>
          <td colSpan={5} className="fw-bold py-1 px-1 text-center align-middle">
            المعدل العام بالنقاط للفصل الدراسي (GPA)
          </td>
          <td className="text-center fw-bold py-1 px-1 align-middle">{sem2 ? sem2.gpa : ''}</td>
        </tr>
      </tbody>
    );
  };

  const headerLineStyle = { fontStyle: 'normal' };
  const nowrapCell = { whiteSpace: 'nowrap' };

  return (
    <div className="container my-2" dir="rtl">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&display=swap');

        /* Sakkal Majalla is a Windows/Office font (not on Google Fonts).
           Amiri is only the fallback if it is not installed. */
        .transcript-container,
        .transcript-container * {
          font-family: 'Sakkal Majalla', 'Amiri', Arial, sans-serif !important;
        }
        .transcript-container table,
        .transcript-container th,
        .transcript-container td {
          border: 1px solid #ccc !important;
          border-collapse: collapse;
          font-size: 13pt !important;
        }
        .transcript-container th {
          text-align: center !important;
          vertical-align: middle !important;
        }
        .transcript-container td {
          vertical-align: middle;
        }
        .transcript-container .row,
        .transcript-container .row p {
          color: #000 !important;
        }
        .transcript-container .signature-block,
        .transcript-container .signature-block * {
          color: #000 !important;
          -webkit-text-fill-color: #000 !important;
          opacity: 1 !important;
        }
        @media print {
          body {
            background: white !important;
            -webkit-print-color-adjust: exact;
          }
          .d-print-none {
            display: none !important;
          }
          .container {
            max-width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .transcript-container {
            border: none !important;
            box-shadow: none !important;
            padding: 2px !important;
            margin: 0 auto !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .year-block {
            page-break-inside: auto;
            break-inside: auto;
          }
          table {
            border-collapse: collapse !important;
          }
          @page {
            size: A4 portrait;
            margin: 5mm;
          }
        }
      `}</style>

      {/* Print button */}
      <div
        className="mb-2 d-print-none"
        style={{ maxWidth: '960px', margin: '0 auto', textAlign: 'left' }}
      >
        <button onClick={handlePrint} className="btn btn-primary btn-sm fw-bold">
          طباعة كشف الدرجات
        </button>
      </div>

      <div
        ref={pageRef}
        dir="rtl"
        className="container bg-white p-3 border shadow-sm transcript-container mx-auto"
        style={{
          maxWidth: '960px',
          fontFamily: '"Sakkal Majalla", "Amiri", Arial, sans-serif',
          fontSize: '13pt',
          textAlign: 'right',
        }}
      >
        {/* Small blank space at the top of the paper */}
        <div style={{ height: '80px' }} />

        {/* Header */}
        <div className="row mb-2" style={{ fontSize: '30px', fontWeight: 900, color: '#000' }}>
          {/* Right side */}
          <div className="col-8">
            {/* marginRight pulls the block inward from the RIGHT edge */}
            <div style={{ marginRight: '40px' }}>
              <p className="mb-0" style={headerLineStyle}>
                كلية {studentData.faculty}
              </p>
              <p className="mb-0" style={headerLineStyle}>
                قسم {studentData.department}
              </p>
              <p className="mb-0" style={headerLineStyle}>
                اسم الطالب: {studentData.name}
              </p>
              <p className="mb-0" style={headerLineStyle}>
                رقم الطالب: {studentData.std_id}
              </p>
            </div>
          </div>
          {/* Left side */}
          {/* In RTL, flex-end = the LEFT edge, so the block sits flush left.
              Lines stay right-aligned inside the block (like the paper original). */}
          <div className="col-4 d-flex" style={{ justifyContent: 'flex-end' }}>
            <div style={{ textAlign: 'right', whiteSpace: 'nowrap', marginLeft: '80px' }}>
              <p className="mb-0" style={headerLineStyle}>
                الدفعة: {studentData.batch}
              </p>
              <p className="mb-0" style={headerLineStyle}>
                المدة الدراسية: {studentData.Program_Duration}
              </p>
              <p className="mb-0" style={headerLineStyle}>
                تاريخ الإصدار: {studentData.issued_date}
              </p>
              <p className="mb-0" style={headerLineStyle}>
                رقم التسلسل: {studentData.serial_no}
              </p>
            </div>
          </div>
        </div>

        <div className="text-center my-2">
          <h4 className="fw-bold" style={{ fontSize: '50px' }}>
            كشف الدرجات
          </h4>
        </div>

        {/* ONE single table: all academic years + summary row */}
        <div className="mb-2">
          <table
            className="table table-bordered table-sm mb-0"
            style={{
              fontSize: '13pt',
              tableLayout: 'fixed',
              width: '100%',
              borderCollapse: 'collapse',
            }}
          >
            {/* 12 columns = 6 per level (code | nameA | nameB | hours | marks | letter).
                code 9.5 + nameA 8.5 + nameB 11.5 + hours 10 + marks 5.5 + letter 5 = 50 */}
            <colgroup>
              <col style={{ width: '9.5%' }} />
              <col style={{ width: '8.5%' }} />
              <col style={{ width: '11.5%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '5.5%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '9.5%' }} />
              <col style={{ width: '8.5%' }} />
              <col style={{ width: '11.5%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '5.5%' }} />
              <col style={{ width: '5%' }} />
            </colgroup>

            {groupedData.map((yearBlock, yearIdx) => renderYearRows(yearBlock, yearIdx))}

            {/* Summary row - dividers line up with the columns above (right -> left):
                المجموع الكلي    = رمز المادة + اسم المادة            (cols 1-3)
                المعدل           = الساعات المعتمدة                    (col 4)
                رمز التقدير      = الدرجة + رمز التقدير                (cols 5-6)
                المعدل التراكمي  = رمز المادة + جزء من اسم المادة       (cols 7-8)
                الدرجة الممنوحة  = باقي اسم المادة + الساعات + الدرجة + التقدير (cols 9-12) */}
            <tbody className="year-block">
              <tr className="bg-light fw-bold text-center" style={{ fontWeight: '700' }}>
                <td colSpan={3} className="py-1 px-1" style={nowrapCell}>
                  <strong>المجموع الكلي:</strong> <Letter value={studentData.grand_total} />
                </td>
                <td colSpan={1} className="py-1 px-1" style={nowrapCell}>
                  <strong>المعدل:</strong> <Letter value={studentData.average} />
                </td>
                <td colSpan={2} className="py-1 px-1" style={nowrapCell}>
                  <strong>رمز التقدير:</strong> <Letter value={studentData.grade} />
                </td>
                <td colSpan={2} className="py-1 px-1" style={nowrapCell}>
                  <strong>المعدل التراكمي (CGPA):</strong> <Letter value={studentData.gpa} />
                </td>
                <td colSpan={4} className="py-1 px-1" style={nowrapCell}>
                  <strong>الدرجة الممنوحة:</strong> {studentData.degree_granted}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Signature Block */}
        <div
          className="d-flex justify-content-between align-items-end mt-5 pt-1 signature-block"
          style={{ pageBreakInside: 'avoid', paddingLeft: '100px', paddingRight: '100px' }}
        >
          {/* saxiixa 1 */}
          <div className="text-center" style={{ width: 'auto', whiteSpace: 'nowrap' }}>
            <hr className="border-dark mb-1" style={{ width: '120px', margin: '0 auto 4px' }} />
            <div style={{ fontSize: '30px', color: '#000', fontWeight: 750, whiteSpace: 'nowrap' }}>
              {studentData.president}
            </div>
            <div style={{ fontSize: '22px', color: '#000' }}>رئيس الجامعة</div>
          </div>

          {/* saxiixa 2 */}
          <div className="text-center" style={{ width: 'auto', whiteSpace: 'nowrap' }}>
            <hr className="border-dark mb-1" style={{ width: '120px', margin: '0 auto 4px' }} />
            <div style={{ fontSize: '30px', color: '#000', fontWeight: 750, whiteSpace: 'nowrap' }}>
              {studentData.registrar}
            </div>
            <div style={{ fontSize: '22px', color: '#000' }}>المسجل الأكاديمي</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArTranscript;